/**
 * Root reducer — combines core feature reducers with dynamic async reducer support.
 * Core reducers (auth, todos, calendar, household, notifications, plugins-toggle)
 * are always loaded. Plugin reducers (e.g. maintenance) are injected dynamically
 * via addAsyncReducer/removeAsyncReducer when plugins are enabled/disabled.
 */

import { combineReducers } from 'redux';
import { authReducer } from '@/features/auth/ducks';
import { todosReducer } from '@/features/todos/ducks';
import { completionLogReducer } from '@/features/todos/completion-log-ducks';
import { calendarReducer } from '@/features/calendar/ducks';
import { calendarConnectionsReducer } from '@/features/calendar-connections';
import { householdReducer } from '@/features/household/ducks';
import { notificationsReducer } from '@/features/notifications/ducks';
import { pluginsToggleReducer } from '@/features/plugins-toggle/ducks';
import { adminSystemInfoReducer } from '@/features/admin-system-info/ducks';
import { backgroundTasksReducer } from '@/features/background-tasks/ducks';

// Core reducers — always loaded regardless of plugin state
const coreReducer = combineReducers({
  auth: authReducer,
  todos: todosReducer,
  completionLog: completionLogReducer,
  calendar: calendarReducer,
  calendarConnections: calendarConnectionsReducer,
  household: householdReducer,
  notifications: notificationsReducer,
  pluginsToggle: pluginsToggleReducer,
  adminSystemInfo: adminSystemInfoReducer,
  backgroundTasks: backgroundTasksReducer,
});

// Async (plugin) reducers — dynamically added/removed at runtime
const asyncReducers: Record<string, (state: any, action: any) => any> = {};

/**
 * Dynamic root reducer that merges core state with any async plugin reducers.
 */
export const rootReducer = (state: any, action: any) => {
  const coreState = coreReducer(state, action);
  return {
    ...coreState,
    ...Object.keys(asyncReducers).reduce((acc, key) => {
      acc[key] = asyncReducers[key](state?.[key], action);
      return acc;
    }, {} as Record<string, any>),
  };
};

/**
 * Add an async reducer for a plugin. Dispatches a dummy action to trigger
 * reducer re-evaluation so the new key appears in state immediately.
 */
export function addAsyncReducer(
  key: string,
  reducer: (state: any, action: any) => any,
  dispatch: (action: any) => void
): void {
  asyncReducers[key] = reducer;
  dispatch({ type: '@@ASYNC_REDUCER_ADDED' });
}

/**
 * Remove an async reducer for a plugin. Dispatches a dummy action to trigger
 * reducer re-evaluation so the key is removed from state.
 */
export function removeAsyncReducer(
  key: string,
  dispatch: (action: any) => void
): void {
  delete asyncReducers[key];
  dispatch({ type: '@@ASYNC_REDUCER_REMOVED' });
}

/**
 * Check whether an async reducer is currently mounted.
 */
export function hasAsyncReducer(key: string): boolean {
  return key in asyncReducers;
}
